import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { resolveRosterAccessibleSections } from "@/features/roster-activity";
import { NavLink } from "@/layouts/AppLayout/types";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { Path } from "@/router";
import useCapabilities, { useCanViewCommitteesBool, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { Section } from "@/types";
import { ALL_SECTIONS_QUERY } from "../queries";
import { useSectionCommitteeManagementEnabled } from "./operations/useSectionCommitteeManagementEnabled";
import { SECTION_NAV_QUERY, SectionNavQueryResponse } from "./queries";

type GetSectionNavLinksOptions = {
  sectionOfficeFacilityId?: string | null;
  canViewFacilities?: boolean;
  canViewCommittees?: boolean;
  sectionCommitteeManagementEnabled?: boolean;
  showRosterActivity?: boolean;
  showSectionPeople?: boolean;
};

export const SECTION_NAV_PEOPLE_LABEL = "People";
export const SECTION_OFFICE_LABEL = "Section Office";
export const VIEW_SECTION_OFFICE_ACTION_LABEL = "View Section Office";
export const SECTION_NAV_ALL_SECTIONS_LABEL = "All Sections";

export type SectionProfileHomePath = "/sections/:sectionId/people/section-people" | "/sections/:sectionId/jobs";

export const SECTION_PEOPLE_PATH = "/sections/:sectionId/people" as const;
export const SECTION_PEOPLE_TABLE_PATH: SectionProfileHomePath = "/sections/:sectionId/people/section-people";
export const SECTION_PEOPLE_CHANGE_REQUESTS_PATH = "/sections/:sectionId/people/change-requests" as const;
export const SECTION_CHANGE_REQUESTS_PATH = "/sections/:sectionId/change-requests" as const;
export const SECTION_JOBS_PATH: SectionProfileHomePath = "/sections/:sectionId/jobs";

export function buildSectionPeopleListPath(sectionId: string): Path {
  return `/people?sectionIds[]=${encodeURIComponent(sectionId)}` as Path;
}

export function getSectionProfileHomePath(showSectionPeople: boolean): SectionProfileHomePath {
  return showSectionPeople ? SECTION_PEOPLE_TABLE_PATH : SECTION_JOBS_PATH;
}

export function getChangeRequestsListPath(showSectionPeople: boolean) {
  return showSectionPeople ? SECTION_PEOPLE_CHANGE_REQUESTS_PATH : SECTION_CHANGE_REQUESTS_PATH;
}

export function getSectionOperationsVisibility({
  sectionOfficeFacilityId,
  canViewFacilities = false,
  canViewCommittees = false,
  sectionCommitteeManagementEnabled = false
}: GetSectionNavLinksOptions = {}) {
  const showCommittees = Boolean(canViewCommittees && sectionCommitteeManagementEnabled);
  const showSectionOfficeFacilityTabs = Boolean(sectionOfficeFacilityId && canViewFacilities);

  return {
    showCommittees,
    showSectionOfficeFacilityTabs,
    showOperations: showCommittees || showSectionOfficeFacilityTabs
  };
}

export const getSectionNavLinks = (
  sectionId: string,
  {
    sectionOfficeFacilityId,
    canViewFacilities = false,
    canViewCommittees = false,
    sectionCommitteeManagementEnabled = false,
    showRosterActivity = false,
    showSectionPeople = false
  }: GetSectionNavLinksOptions = {}
): Record<string, NavLink<Path>[]> => {
  const showSectionOfficeLink = Boolean(sectionOfficeFacilityId && canViewFacilities);
  const { showOperations } = getSectionOperationsVisibility({
    sectionOfficeFacilityId,
    canViewFacilities,
    canViewCommittees,
    sectionCommitteeManagementEnabled
  });

  const jobsLink = {
    domain: DOMAIN.SECTIONS,
    label: "Jobs",
    link: {
      to: SECTION_JOBS_PATH,
      params: { sectionId }
    },
    icon: "business_center" as const
  };

  const billingLink = {
    domain: DOMAIN.SECTIONS,
    label: "Billing",
    link: {
      to: "/sections/:sectionId/billing/" as Path,
      params: { sectionId }
    },
    icon: "card_membership" as const
  };

  const operationsLink = showOperations && {
    domain: DOMAIN.SECTIONS,
    label: "Operations",
    link: {
      to: "/sections/:sectionId/operations/" as Path,
      params: { sectionId }
    },
    icon: "flowsheet" as const
  };

  const peopleWorkspaceLink = {
    domain: DOMAIN.SECTIONS,
    label: SECTION_NAV_PEOPLE_LABEL,
    link: {
      to: SECTION_PEOPLE_PATH as Path,
      params: { sectionId }
    },
    icon: "group" as const,
    matchPaths: [
      "/sections/:sectionId/change-requests" as Path,
      "/sections/:sectionId/roster-activity" as Path,
      "/sections/:sectionId/audit-log" as Path
    ]
  };

  const changeRequestsLink = {
    domain: DOMAIN.SECTIONS,
    label: "Change Requests",
    link: {
      to: "/sections/:sectionId/change-requests" as Path,
      params: { sectionId }
    },
    icon: "pending_actions" as const
  };

  const rosterActivityLink = showRosterActivity && {
    domain: DOMAIN.SECTIONS,
    label: "Roster Activity",
    link: {
      to: "/sections/:sectionId/roster-activity" as Path,
      params: { sectionId }
    },
    icon: "groups" as const
  };

  const auditLogLink = {
    domain: DOMAIN.SECTIONS,
    label: "Audit Log",
    link: {
      to: "/sections/:sectionId/audit-log" as Path,
      params: { sectionId }
    },
    icon: "quick_reference_all" as const
  };

  const peopleDirectoryLink = {
    domain: DOMAIN.EVERYONE,
    label: SECTION_NAV_PEOPLE_LABEL,
    link: {
      to: buildSectionPeopleListPath(sectionId)
    },
    icon: "group" as const,
    dividerBefore: true,
    externalLink: true
  };

  const sectionOfficeLink = showSectionOfficeLink && {
    domain: DOMAIN.FACILITIES,
    label: SECTION_OFFICE_LABEL,
    link: {
      to: "/facilities/:facilityId" as Path,
      params: { facilityId: sectionOfficeFacilityId! }
    },
    icon: "store" as const,
    externalLink: true,
    dividerBefore: showSectionPeople
  };

  const allSectionsLink = {
    domain: DOMAIN.SECTIONS,
    label: SECTION_NAV_ALL_SECTIONS_LABEL,
    link: {
      to: "/sections" as Path
    },
    icon: "domain" as const,
    externalLink: true,
    dividerBefore: showSectionPeople && !showSectionOfficeLink
  };

  const sectionLinks = (
    showSectionPeople
      ? [peopleWorkspaceLink, jobsLink, billingLink, operationsLink, sectionOfficeLink, allSectionsLink]
      : [
          jobsLink,
          changeRequestsLink,
          rosterActivityLink,
          billingLink,
          operationsLink,
          auditLogLink,
          peopleDirectoryLink,
          showSectionOfficeLink && {
            domain: DOMAIN.FACILITIES,
            label: SECTION_OFFICE_LABEL,
            link: {
              to: "/facilities/:facilityId" as Path,
              params: { facilityId: sectionOfficeFacilityId! }
            },
            icon: "store" as const,
            externalLink: true
          },
          {
            domain: DOMAIN.SECTIONS,
            label: SECTION_NAV_ALL_SECTIONS_LABEL,
            link: {
              to: "/sections" as Path
            },
            icon: "domain" as const,
            externalLink: true
          }
        ]
  ).filter(Boolean) as NavLink<Path>[];

  return {
    "Manage Section": sectionLinks
  };
};

type WithAllSectionsFlyoutOptions = {
  homeTo?: SectionProfileHomePath;
};

export function withAllSectionsFlyout(
  navLinks: Record<string, NavLink<Path>[]>,
  sections: Section[] | undefined,
  { homeTo = SECTION_JOBS_PATH }: WithAllSectionsFlyoutOptions = {}
): Record<string, NavLink<Path>[]> {
  if (!sections?.length) {
    return navLinks;
  }

  const manageSectionLinks = navLinks["Manage Section"];
  const allSectionsIndex = manageSectionLinks.findIndex((link) => link.label === SECTION_NAV_ALL_SECTIONS_LABEL);
  if (allSectionsIndex === -1) {
    return navLinks;
  }

  const flyoutLinks = [...sections]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .map((section) => ({
      label: section.name,
      link: {
        to: homeTo,
        params: { sectionId: section.id }
      }
    }));

  const updatedLinks = [...manageSectionLinks];
  updatedLinks[allSectionsIndex] = {
    ...manageSectionLinks[allSectionsIndex],
    trailingIcon: "navigate_next",
    flyoutLinks
  };

  return {
    ...navLinks,
    "Manage Section": updatedLinks
  };
}

export function useSectionNavLinks(sectionId?: string) {
  const flags = useFlags();
  const { hasRole, loaded: capabilitiesLoaded, sectionLegacyIds, sectionLegacyIdsLoading } = useCapabilities();
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewSection = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.viewSection
  });
  const canViewPeople = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.viewPeople
  });
  const canViewCommittees = useCanViewCommitteesBool();
  const sectionCommitteeManagementEnabled = useSectionCommitteeManagementEnabled();
  const showRosterActivity = flags["backoffice-section-roster-activity"] === true;
  const showSectionPeople = flags["backoffice-section-people"] === true;
  const isSectionStaff = capabilitiesLoaded && (hasRole(ROLE.SECTION_STAFF) || hasRole(ROLE.SECTION_ADMIN));
  const shouldLoadSections = capabilitiesLoaded && canViewSection;
  const { data: sectionNavData } = useQuery<SectionNavQueryResponse>(SECTION_NAV_QUERY, {
    variables: { sectionId: sectionId ?? "" },
    skip: !sectionId || !canViewFacilities,
    fetchPolicy: "cache-first"
  });
  const { data: allSectionsData, loading: allSectionsLoading } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: !shouldLoadSections,
    fetchPolicy: "cache-first"
  });

  return useMemo(() => {
    const navLinks = getSectionNavLinks(sectionId ?? "", {
      sectionOfficeFacilityId: sectionNavData?.section?.sectionOffice?.id,
      canViewFacilities,
      canViewCommittees,
      sectionCommitteeManagementEnabled,
      showRosterActivity,
      showSectionPeople
    });

    const sectionsStillLoading =
      (isSectionStaff && sectionLegacyIdsLoading) || (shouldLoadSections && allSectionsLoading);

    const accessibleSections = sectionsStillLoading
      ? undefined
      : resolveRosterAccessibleSections({
          canViewSection,
          isSectionStaff,
          assignedSectionIds: sectionLegacyIds,
          allSections: allSectionsData?.sections ?? []
        });

    return withAllSectionsFlyout(navLinks, accessibleSections, {
      homeTo: getSectionProfileHomePath(showSectionPeople && canViewPeople)
    });
  }, [
    sectionId,
    sectionNavData?.section?.sectionOffice?.id,
    canViewFacilities,
    canViewSection,
    canViewPeople,
    canViewCommittees,
    sectionCommitteeManagementEnabled,
    showRosterActivity,
    showSectionPeople,
    isSectionStaff,
    sectionLegacyIds,
    sectionLegacyIdsLoading,
    allSectionsLoading,
    shouldLoadSections,
    allSectionsData?.sections
  ]);
}
